import React, {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

/**
 * Organization shape tracked in developer mode. Kept minimal but open so
 * consumers written against OrganizationContext (which carry richer org
 * objects) continue to type-check when swapped to developer mode.
 */
export interface DeveloperOrg {
  id: number;
  name: string;
  [key: string]: unknown;
}

export interface BillingStatus {
  active: boolean;
}

/**
 * Value exposed by DeveloperContext. Mirrors the OrganizationContext-compatible
 * shape so downstream consumers work unmodified in developer mode.
 */
export interface DeveloperContextValue {
  // Developer-specific state
  selectedOrgId: number | null;
  setSelectedOrgId: Dispatch<SetStateAction<number | null>>;
  selectedOrg: DeveloperOrg | null;
  setSelectedOrg: Dispatch<SetStateAction<DeveloperOrg | null>>;

  // Mock organization context interface for compatibility
  currentOrg: DeveloperOrg | null;
  currentOrgId: number | null;
  userOrgs: DeveloperOrg[];
  billingStatus: BillingStatus;
  loading: boolean;
  orgError: string | null;

  // Developer mode flag
  isDeveloperMode: boolean;
}

const DeveloperContext = createContext<DeveloperContextValue | undefined>(
  undefined
);

export const useDeveloper = (): DeveloperContextValue => {
  const context = useContext(DeveloperContext);
  if (!context) {
    throw new Error('useDeveloper must be used within a DeveloperProvider');
  }
  return context;
};

interface DeveloperProviderProps {
  children: ReactNode;
}

export const DeveloperProvider = ({
  children,
}: DeveloperProviderProps): React.JSX.Element => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<DeveloperOrg | null>(null);

  const value: DeveloperContextValue = {
    // Developer-specific state
    selectedOrgId,
    setSelectedOrgId,
    selectedOrg,
    setSelectedOrg,

    // Mock organization context interface for compatibility
    currentOrg: selectedOrg,
    currentOrgId: selectedOrgId,
    userOrgs: [],
    billingStatus: { active: true }, // Developers always have access
    loading: false,
    orgError: null,

    // Developer mode flag
    isDeveloperMode: true,
  };

  return (
    <DeveloperContext.Provider value={value}>
      {children}
    </DeveloperContext.Provider>
  );
};

export default DeveloperContext;
